import { defineStore } from 'pinia';
import useApi from '@/composeables/useApi';
import { CenterWarning, Checklist, ListWarning, PaginatedResponse, PoliceWarning } from '@/constants/types';

const mergeById = <T extends { id: number }>(existing: T[], incoming: T[]) => {
  const merged = new Map<number, T>();

  existing.forEach((item) => {
    merged.set(item.id, item);
  });

  incoming.forEach((item) => {
    merged.set(item.id, item);
  });

  return Array.from(merged.values());
};

export enum WarningRequestType {
  Regular = 'regular',
  Security = 'security',
  Police = 'police',
}

type WarningQuery = {
  searchQuery?: string;
  counties?: string;
  sectors?: string;
};

export const useWarningsStore = defineStore('warningsStore', {
  state: () => ({
    warnings: [] as ListWarning[],
    securityWarnings: [] as CenterWarning[],
    policeWarnings: [] as PoliceWarning[],
    warningsChecklist: null as Checklist,
    loading: false,
    warningsSearchQuery: '',
    warningsNextPage: null as number | null,
    securityWarningsNextPage: null as number | null,
    policeWarningsNextPage: null as number | null,
    warningsTotal: 0,
    securityWarningsTotal: 0,
    policeWarningsTotal: 0,
    lastWarningQueries: {} as Partial<Record<WarningRequestType, WarningQuery>>,
  }),
  actions: {
    async fetchWarnings(type: WarningRequestType, searchQuery?: string, toNextPage?: boolean, counties?: string, sectors?: string) {
      const { apiGet } = useApi();

      // Keep search/filters when paging, so the paged data and the total stay in sync with the list
      if (toNextPage) {
        const lastQuery = this.lastWarningQueries[type] || {};
        searchQuery = searchQuery ?? lastQuery.searchQuery;
        counties = counties ?? lastQuery.counties;
        sectors = sectors ?? lastQuery.sectors;
      } else {
        this.lastWarningQueries[type] = { searchQuery, counties, sectors };
      }

      this.loading = true;
      const nextPage = this.getNextPage(type);
      const apiPage = toNextPage && nextPage ? nextPage : 1;
      let endpoint = `/warnings/${type}?page=${apiPage}`;

      if (searchQuery) {
        endpoint += `&query=${searchQuery}`;
      }

      if (counties) {
        endpoint += `&counties=${counties}`;
      }

      if (sectors) {
        endpoint += `&sectors=${sectors}`;
      }

      const res = await apiGet<PaginatedResponse<ListWarning>>(endpoint, null, false);

      if (res) {
        if (type === WarningRequestType.Police) {
          this.policeWarningsNextPage = res.next_page_url ? res.current_page + 1 : null;
          this.policeWarnings = toNextPage ? [...this.policeWarnings, ...res.data] : res.data || [];
          this.policeWarningsTotal = res.total ?? this.policeWarnings.length;
        } else if (type === WarningRequestType.Security) {
          this.securityWarningsNextPage = res.next_page_url ? res.current_page + 1 : null;
          this.securityWarnings = toNextPage ? [...this.securityWarnings, ...res.data] : res.data || [];
          this.securityWarningsTotal = res.total ?? this.securityWarnings.length;
        } else {
          this.warningsNextPage = res.next_page_url ? res.current_page + 1 : null;
          this.warnings = toNextPage ? [...this.warnings, ...res.data] : res.data || [];
          this.warningsTotal = res.total ?? this.warnings.length;
        }
      }

      this.loading = false;
    },
    async fetchAllWarnings() {
      await this.fetchWarnings(WarningRequestType.Regular);
      await this.fetchWarnings(WarningRequestType.Security);
      await this.fetchWarnings(WarningRequestType.Police);
    },
    async fetchWarningChecklist() {
      const { apiGet } = useApi();
      const res = await apiGet<Checklist[]>('/checklists/invoicefraudchecklist');
      
      if (res) {
        this.warningsChecklist = res;
      }
    },
    async fetchSecurityWarningById(id: number) {
      if (!Number.isFinite(id)) {
        return null;
      }

      const existingWarning = this.getSecurityWarningById(id);

      if (existingWarning) {
        return existingWarning;
      }

      const { apiGet } = useApi();
      const directEndpoints = [
        `/warnings/security/${id}`,
        `/warnings/security-warning/${id}`,
      ];

      for (const endpoint of directEndpoints) {
        const directWarning = await apiGet<CenterWarning>(endpoint, null, false);

        if (directWarning?.id) {
          this.securityWarnings = mergeById(this.securityWarnings, [directWarning]);
          return directWarning;
        }
      }

      let page = 1;

      while (page) {
        const response = await apiGet<PaginatedResponse<CenterWarning>>(`/warnings/security?page=${page}`, null, false);

        if (!response) {
          break;
        }

        this.securityWarnings = mergeById(this.securityWarnings, response.data || []);

        const foundWarning = response.data?.find((warning) => warning.id === id) || null;

        if (foundWarning) {
          this.securityWarningsNextPage = response.next_page_url ? response.current_page + 1 : null;
          return foundWarning;
        }

        page = response.next_page_url ? response.current_page + 1 : 0;
      }

      return null;
    },
    async fetchMoreWarnings(type: WarningRequestType) {
      // Without a next page the request would refetch page 1 and append duplicates
      if (!this.getNextPage(type)) {
        return;
      }

      await this.fetchWarnings(type, null, true);
    },
    async onWarningsSearch(input: string, type: WarningRequestType) {
      this.warningsSearchQuery = input;
      await this.fetchWarnings(type, input);
    },
  },
  getters: {
    getNextPage(state) {
      return (type: WarningRequestType) => {
        if (type === WarningRequestType.Police) {
          return state.policeWarningsNextPage;
        }

        if (type === WarningRequestType.Security) {
          return state.securityWarningsNextPage;
        }

        return state.warningsNextPage;
      };
    },
    getWarningById(state) {
      return (id) => state.warnings.find(warning => warning.id === id);
    },
    getSecurityWarningById(state) {
      return (id) => state.securityWarnings.find(warning => warning.id === id);
    },
  },
});
